import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '@auth0/auth0-angular';

import { catchError, EMPTY, filter, finalize, interval, of, retry, startWith, switchMap, tap } from 'rxjs';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ActiveNotification } from '../../models/responses/notifications.response';

import { environment } from '../../../../environments/environment';

const POLL_INTERVAL_MS = 300_000;
const STORAGE_KEY = 'notifica_dismissed';

function loadPersistedDismissals(): Set<string> {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
    return new Set(Array.isArray(raw) ? raw.filter((id): id is string => typeof id === 'string') : []);
  } catch {
    return new Set();
  }
}

@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);

  private readonly _notifications = signal<ActiveNotification[]>([]);
  private readonly _dismissed = signal(loadPersistedDismissals());
  private readonly _loading = signal(false);
  private readonly _loaded = signal(false);
  private readonly _error = signal<string | null>(null);

  readonly banners = computed(() => this.activeOfType('BANNER'));
  readonly popups = computed(() => this.activeOfType('POPUP'));
  /** Campanhas do carrossel da home. Ao contrário dos outros, várias podem estar ativas. */
  readonly homeBanners = computed(() => this.activeOfType('HOME_BANNER'));
  readonly loading = computed(() => this._loading());
  /** Primeira busca já respondeu. Distingue "ainda não sei" de "não há nada", que em
   *  `_notifications` são os dois um array vazio — é o que o skeleton precisa saber. */
  readonly loaded = computed(() => this._loaded());
  readonly error = computed(() => this._error());

  constructor() {
    this.auth.isAuthenticated$
      .pipe(
        switchMap((isAuthenticated) => {
          if (!isAuthenticated) {
            this.reset();
            return EMPTY;
          }

          return this.auth.user$.pipe(
            filter((user): user is { email: string } => !!user?.email),
            switchMap((user) =>
              interval(POLL_INTERVAL_MS).pipe(
                startWith(0),
                tap(() => {
                  this._loading.set(true);
                  this._error.set(null);
                }),
                switchMap(() =>
                  this.http
                    .get<ActiveNotification[]>(`${environment.NOTIFICA_API_URL}/v1/notifications`, {
                      params: { userEmail: user.email },
                      headers: { 'x-consumer-secret': environment.NOTIFICA_CONSUMER_SECRET }
                    })
                    .pipe(
                      retry({ count: 2, delay: 1000 }),
                      catchError((err) => {
                        console.error('Erro ao buscar notificações:', err);
                        this._error.set('Falha ao carregar notificações');
                        return of([]);
                      }),
                      finalize(() => this._loading.set(false))
                    )
                )
              )
            )
          );
        }),
        takeUntilDestroyed()
      )
      .subscribe((notifications) => {
        this._notifications.set(notifications);
        this._loaded.set(true);
      });
  }

  /** `forever` persiste em localStorage — vale por navegador, não entre dispositivos. */
  dismiss(id: string, forever = false): void {
    this._dismissed.update((set) => new Set([...set, id]));

    if (forever) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify([...this._dismissed()]));
      } catch {
        // Storage cheio ou bloqueado: o dismiss da sessão continua valendo.
      }
    }
  }

  private activeOfType(type: ActiveNotification['type']): ActiveNotification[] {
    const dismissed = this._dismissed();
    const now = Date.now();
    return this._notifications().filter((n) => {
      if (n.type !== type) return false;
      if (dismissed.has(n.id)) return false;
      if (n.expiresAt && new Date(n.expiresAt).getTime() <= now) return false;
      return true;
    });
  }

  private reset(): void {
    this._notifications.set([]);
    // Recarrega do storage em vez de zerar: o "não mostrar novamente" sobrevive ao logout.
    this._dismissed.set(loadPersistedDismissals());
    this._loading.set(false);
    this._loaded.set(false);
    this._error.set(null);
  }
}
